import React, { useState, useRef, useEffect } from 'react';
import { useEditor } from '../contexts/EditorContext';
import { Upload } from 'lucide-react';

const EditableImage = ({ 
  section, 
  field, 
  src, 
  alt = '', 
  className = '',
  style = {}
}) => {
  const { isEditorMode, getContent, updateContent } = useEditor();
  const [currentSrc, setCurrentSrc] = useState(src);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Get image src from editor or use prop as fallback
    const content = getContent(section, field, src);
    setCurrentSrc(content);
  }, [section, field, src, getContent]);

  const handleClick = (e) => {
    if (isEditorMode) {
      e.preventDefault();
      e.stopPropagation();
      setShowUploadModal(true);
      setUrlInput(currentSrc);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create a data URL for the image
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        setCurrentSrc(dataUrl);
        updateContent(section, field, dataUrl, 'image');
        setShowUploadModal(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      setCurrentSrc(urlInput.trim());
      updateContent(section, field, urlInput.trim(), 'image');
      setShowUploadModal(false);
    }
  };

  if (!isEditorMode) {
    // Normal mode
    return <img src={currentSrc} alt={alt} className={className} style={style} />;
  }

  return (
    <>
      <div className="relative group">
        <img 
          src={currentSrc} 
          alt={alt} 
          className={`${className} cursor-pointer transition-all`}
          style={style}
          onClick={handleClick}
        />
        {/* Hover overlay */}
        <div 
          className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center cursor-pointer"
          onClick={handleClick}
        >
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-lg px-4 py-2 flex items-center space-x-2">
            <Upload className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-600">Change Image</span>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Change Image</h3>
            
            {/* Current Image Preview */}
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Current Image:</p>
              <img src={currentSrc} alt="Preview" className="w-full h-32 object-cover rounded border" />
            </div>

            {/* Upload from Computer */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload from Computer
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Choose File</span>
              </button>
            </div>

            {/* Or use URL */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or Enter Image URL
              </label>
              <input
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleUrlSubmit}
                disabled={!urlInput.trim()}
                className={`mt-2 w-full px-4 py-2 rounded-lg font-medium ${
                  urlInput.trim()
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Use This URL
              </button>
            </div>

            {/* Close button */}
            <button
              onClick={() => setShowUploadModal(false)}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-900 px-4 py-2 rounded-lg font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EditableImage;
